import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import API from './services/api';
import { getAppPath, isDemoMode } from './utils/runtimeMode';

const TEAL = '#1fa97a';
const TEAL_DARK = '#0f6e56';
const TEAL_BG = '#e8f7f2';
const BLUE = '#2563eb';
const TEXT = '#1f2937';
const MUTED = '#64748b';
const BORDER = '#dbe3ea';
const SURFACE = '#ffffff';
const PAGE_BG = '#f5f7fb';
const DANGER = '#b91c1c';
const DANGER_BG = '#fef2f2';
const WARNING = '#b45309';
const WARNING_BG = '#fffbeb';

const provinces = [
  { value: '', label: 'Select province' },
  { value: 'AB', label: 'AB - Alberta' },
  { value: 'BC', label: 'BC - British Columbia' },
  { value: 'MB', label: 'MB - Manitoba' },
  { value: 'NB', label: 'NB - New Brunswick' },
  { value: 'NL', label: 'NL - Newfoundland and Labrador' },
  { value: 'NS', label: 'NS - Nova Scotia' },
  { value: 'NT', label: 'NT - Northwest Territories' },
  { value: 'NU', label: 'NU - Nunavut' },
  { value: 'ON', label: 'ON - Ontario' },
  { value: 'PE', label: 'PE - Prince Edward Island' },
  { value: 'QC', label: 'QC - Quebec' },
  { value: 'SK', label: 'SK - Saskatchewan' },
  { value: 'YT', label: 'YT - Yukon' }
];

const years = Array.from({ length: 18 }, (_, index) => String(new Date().getFullYear() + index));
const days = Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, '0'));
const months = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' }
];

const destinationLists = [
  'Daily Pre-job Contractor List',
  'Formal Hazard Assessment contractor references',
  'Toolbox Meeting contractor list'
];

const contractorPrequalificationManagerEmails = new Set([
  'safety@diamondvalley.town',
  'cao@diamondvalley.town',
  'doreen@davg.ca'
]);

const demoContractorPrequalificationManagerEmails = new Set([
  'ctdemo.cao@proton.me'
]);

const CONTRACTOR_RECORDS_PAGE_SIZE = 10;

const page = {
  minHeight: 'calc(100vh - 64px)',
  background: PAGE_BG,
  padding: '24px 16px 48px',
  color: TEXT,
  fontFamily: 'inherit'
};

const shell = {
  maxWidth: '1180px',
  margin: '0 auto'
};

const card = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  padding: '18px',
  marginBottom: '16px'
};

const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '14px'
};

const compactGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '10px'
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  color: MUTED,
  fontWeight: 700,
  marginBottom: '4px'
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 10px',
  border: `1px solid ${BORDER}`,
  borderRadius: '6px',
  fontSize: '14px',
  color: TEXT,
  background: SURFACE,
  fontFamily: 'inherit',
  outline: 'none'
};

const textareaStyle = {
  ...inputStyle,
  minHeight: '82px',
  resize: 'vertical'
};

const sectionTitle = {
  fontSize: '17px',
  fontWeight: 700,
  margin: '0 0 12px',
  color: TEXT
};

const paragraph = {
  fontSize: '14px',
  lineHeight: 1.55,
  margin: '0 0 10px',
  color: TEXT
};

const buttonBase = {
  border: `1px solid ${BORDER}`,
  borderRadius: '6px',
  padding: '9px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};

const getInitialDateParts = () => ({ day: '', month: '', year: '' });

const getInitialForm = () => ({
  id: '',
  dateOfHire: '',
  companyName: '',
  streetAddress: '',
  city: '',
  province: '',
  postalCode: '',
  primaryContactName: '',
  primaryContactPhone: '',
  primaryContactEmail: '',
  additionalContacts: [],
  servicesProvided: '',
  wcbAccountNumber: '',
  wcbExempt: false,
  wcbExemptionExplanation: '',
  insuranceCompany: '',
  policyNumber: '',
  minimumPolicy: 'No',
  minimumPolicyExplanation: '',
  addedNamedInsured: 'No',
  addedNamedInsuredNote: '',
  insuranceExpiry: getInitialDateParts(),
  businessLicense: 'No',
  businessLicenseNumber: '',
  businessLicenseExpiry: getInitialDateParts(),
  businessLicenseExemption: '',
  corSecor: 'No',
  corSecorNumber: '',
  corSecorExpiry: getInitialDateParts(),
  programElementsEvaluation: 'No',
  programElementsEvaluationNote: '',
  primeContractor: 'No',
  primeContractorComments: '',
  primeContractorAttachments: [],
  trainingCertificatesReviewed: 'No',
  trainingCertificateAttachments: [],
  trainingVerificationExplanation: '',
  extraordinaryHazards: 'No',
  formalHazardAssessmentAttachments: [],
  preExistingHazards: '',
  hazardControls: '',
  generalAttachments: [],
  finalAcknowledgement: false
});

const makeRecordId = () => `contractor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getDisplayName = (user) => {
  const name = [user?.firstName || user?.FIRST_NAME, user?.lastName || user?.LAST_NAME].filter(Boolean).join(' ').trim();
  return name || user?.displayName || user?.DISPLAY_NAME || user?.username || user?.email || user?.EMAIL || 'Logged-in user';
};

const normalizeEmail = (value = '') => String(value || '').trim().toLowerCase();

const getAuthEmail = (user) => normalizeEmail(user?.email || user?.EMAIL || user?.username || user?.USERNAME);

const normalizeSearchValue = (value = '') => String(value || '').trim().toLowerCase();

const contractorMatchesFilter = (record, filter) => {
  const normalizedFilter = normalizeSearchValue(filter);
  if (!normalizedFilter) return true;

  return [
    record.companyName,
    record.primaryContactName,
    record.primaryContactEmail,
    record.primaryContactPhone,
    record.city,
    record.province,
    record.servicesProvided,
    record.businessLicenseNumber,
    record.corSecorNumber
  ].some((value) => normalizeSearchValue(value).includes(normalizedFilter));
};

const datePartsToIso = (parts) => {
  if (!parts?.day || !parts?.month || !parts?.year) return '';
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const formatDateParts = (parts) => datePartsToIso(parts) || 'Not set';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const readAttachmentFile = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  const metadata = {
    name: file.name,
    size: file.size,
    type: file.type,
    addedAt: new Date().toISOString()
  };

  reader.onload = () => resolve({
    ...metadata,
    dataUrl: typeof reader.result === 'string' ? reader.result : ''
  });
  reader.onerror = () => resolve({ ...metadata, dataUrl: '' });
  reader.readAsDataURL(file);
});

const normalizeFiles = (fileList) => Promise.all(Array.from(fileList || []).map(readAttachmentFile));

const normalizeAttachmentApiUrl = (url = '') => {
  if (!url) return '';

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith('/api/contractorPrequalification/attachments/')) {
      return `${parsed.pathname}${parsed.search || ''}`;
    }
  } catch (error) {
    // Fall through to the original value for non-URL strings.
  }

  return url;
};

const openAttachmentInNewTab = async (file) => {
  const directDataUrl = file?.dataUrl && !file?.downloadUrl ? file.dataUrl : '';
  const downloadUrl = file?.downloadUrl || '';
  const viewUrl = directDataUrl || downloadUrl;

  if (!viewUrl) return;

  const openedWindow = window.open('', '_blank');
  if (openedWindow) {
    openedWindow.opener = null;
  }

  if (directDataUrl) {
    if (openedWindow) {
      openedWindow.location.href = directDataUrl;
    } else {
      window.open(directDataUrl, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  try {
    const response = await API.get(normalizeAttachmentApiUrl(downloadUrl), { responseType: 'blob' });
    const contentType = response.headers?.['content-type'] || file?.mimeType || file?.type || 'application/octet-stream';

    if (contentType.includes('text/html')) {
      openedWindow?.close();
      alert('Unable to open attachment. Please sign in and try again.');
      return;
    }

    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
    if (openedWindow) {
      openedWindow.location.href = blobUrl;
    } else {
      const fallbackWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!fallbackWindow) {
        alert('Popup blocked. Allow popups for this site to view the attachment in a new tab.');
      }
    }
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    openedWindow?.close();
    console.error('Failed to open contractor attachment:', error);
    if (error?.response?.status === 401 || error?.response?.status === 440) {
      window.location.href = getAppPath('/login');
      return;
    }
    alert('Unable to open attachment. Please try again.');
  }
};

const getExpiryAlert = (record, fieldName, label) => {
  const value = datePartsToIso(record?.[fieldName]);
  if (!value) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiry = new Date(`${value}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return null;

  const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return `${label} expired ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago.`;
  if (diffDays <= 30) return `${label} expires in ${diffDays} day${diffDays === 1 ? '' : 's'}.`;
  return null;
};

function Chip({ children, tone = 'neutral' }) {
  const tones = {
    neutral: { background: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
    teal: { background: TEAL_BG, color: TEAL_DARK, border: TEAL },
    blue: { background: '#eff6ff', color: BLUE, border: '#bfdbfe' },
    warning: { background: WARNING_BG, color: WARNING, border: '#fde68a' },
    danger: { background: DANGER_BG, color: DANGER, border: '#fecaca' }
  };
  const selected = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '999px',
      background: selected.background,
      color: selected.color,
      border: `1px solid ${selected.border}`,
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: 1.2
    }}>
      {children}
    </span>
  );
}

function TextInput({ label, value, onChange, type = 'text', required = false, placeholder = '', disabled = false }) {
  return (
    <label>
      <span style={labelStyle}>{label}{required ? ' *' : ''}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        style={{ ...inputStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'text' }}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, required = false, placeholder = '', disabled = false }) {
  return (
    <label>
      <span style={labelStyle}>{label}{required ? ' *' : ''}</span>
      <textarea
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        style={{ ...textareaStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'text' }}
      />
    </label>
  );
}

function SelectInput({ label, value, onChange, options, required = false, disabled = false }) {
  return (
    <label>
      <span style={labelStyle}>{label}{required ? ' *' : ''}</span>
      <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} style={{ ...inputStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function YesNoField({ label, value, onChange, disabled = false }) {
  return (
    <div>
      <span style={labelStyle}>{label} *</span>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {['No', 'Yes'].map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(option)}
            style={{
              ...buttonBase,
              background: value === option ? TEAL : SURFACE,
              color: value === option ? '#fff' : TEXT,
              borderColor: value === option ? TEAL : BORDER,
              minWidth: '74px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled && value !== option ? 0.65 : 1
            }}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function DatePartsInput({ label, value, onChange, required = false, disabled = false }) {
  const updatePart = (part, nextValue) => onChange({ ...value, [part]: nextValue });

  return (
    <div>
      <span style={labelStyle}>{label}{required ? ' *' : ''}</span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: '8px' }}>
        <select value={value.day} disabled={disabled} onChange={(event) => updatePart('day', event.target.value)} style={{ ...inputStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <option value="">Day</option>
          {days.map((day) => <option key={day} value={day}>{day}</option>)}
        </select>
        <select value={value.month} disabled={disabled} onChange={(event) => updatePart('month', event.target.value)} style={{ ...inputStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <option value="">Month</option>
          {months.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
        </select>
        <select value={value.year} disabled={disabled} onChange={(event) => updatePart('year', event.target.value)} style={{ ...inputStyle, background: disabled ? '#f8fafc' : SURFACE, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <option value="">Year</option>
          {years.map((year) => <option key={year} value={year}>{year}</option>)}
        </select>
      </div>
    </div>
  );
}

function FileInput({ label, files, onChange, required = false, multiple = true, disabled = false }) {
  const handleFileSelection = async (event) => {
    const selectedFiles = await normalizeFiles(event.target.files);
    onChange([...files, ...selectedFiles]);
    event.target.value = '';
  };

  return (
    <div>
      <span style={labelStyle}>{label}{required ? ' *' : ''}</span>
      {!disabled && (
        <input
          type="file"
          multiple={multiple}
          onChange={handleFileSelection}
          style={{ ...inputStyle, padding: '7px 10px' }}
        />
      )}
      {disabled && files.length === 0 && (
        <div style={{ ...inputStyle, background: '#f8fafc', color: MUTED }}>No files attached</div>
      )}
      {files.length > 0 && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {files.map((file, index) => (
            <span key={`${file.name}-${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '999px', padding: '4px 8px', fontSize: '12px' }}>
              {file.name}
              {file.dataUrl || file.downloadUrl ? (
                <button
                  type="button"
                  onClick={() => openAttachmentInNewTab(file)}
                  style={{ border: 'none', background: 'transparent', color: BLUE, fontWeight: 700, textDecoration: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: '12px' }}
                >
                  View
                </button>
              ) : disabled ? (
                <span style={{ color: MUTED }}>Preview unavailable</span>
              ) : null}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onChange(files.filter((_, fileIndex) => fileIndex !== index))}
                  style={{ border: 'none', background: 'transparent', color: DANGER, cursor: 'pointer', fontWeight: 700, padding: 0 }}
                  aria-label={`Remove ${file.name}`}
                >
                  x
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FormSection({ title, children, note }) {
  return (
    <section style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '12px' }}>
        <h2 style={{ ...sectionTitle, marginBottom: 0 }}>{title}</h2>
        {note && <span style={{ color: MUTED, fontSize: '12px' }}>{note}</span>}
      </div>
      {children}
    </section>
  );
}

export default function ContractorPrequalification() {
  const { user } = useAuth();
  const userName = getDisplayName(user);
  const userEmail = getAuthEmail(user);
  const [serverCanManageContractorPrequalification, setServerCanManageContractorPrequalification] = useState(false);
  const canManageContractorPrequalification = contractorPrequalificationManagerEmails.has(userEmail)
    || (isDemoMode() && demoContractorPrequalificationManagerEmails.has(userEmail))
    || serverCanManageContractorPrequalification;
  const [records, setRecords] = useState([]);
  const [mode, setMode] = useState('idle');
  const [form, setForm] = useState(getInitialForm());
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [recordsTab, setRecordsTab] = useState('active');
  const [recordFilter, setRecordFilter] = useState('');
  const [recordPage, setRecordPage] = useState(1);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadRecords = async () => {
      setIsLoadingRecords(true);
      try {
        const response = await API.get('/api/contractorPrequalification/records');
        if (!cancelled) {
          setRecords(Array.isArray(response.data?.records) ? response.data.records : []);
          setServerCanManageContractorPrequalification(!!response.data?.canManage);
        }
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          setServerCanManageContractorPrequalification(false);
          setValidationErrors([error?.response?.data?.error || error.message || 'Unable to load contractor records.']);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRecords(false);
        }
      }
    };

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, []);

  const activeRecords = useMemo(() => records.filter((record) => record.status !== 'ARCHIVED'), [records]);
  const archivedRecords = useMemo(() => records.filter((record) => record.status === 'ARCHIVED'), [records]);
  const employeeRecords = useMemo(() => records.filter((record) => record.status === 'APPROVED'), [records]);
  const selectedRecord = useMemo(() => records.find((record) => record.id === selectedRecordId) || null, [records, selectedRecordId]);
  const managerVisibleRecords = recordsTab === 'archived' ? archivedRecords : activeRecords;
  const unfilteredVisibleRecords = canManageContractorPrequalification ? managerVisibleRecords : employeeRecords;
  const visibleRecords = useMemo(() => unfilteredVisibleRecords.filter((record) => contractorMatchesFilter(record, recordFilter)), [unfilteredVisibleRecords, recordFilter]);
  const recordPageCount = Math.max(1, Math.ceil(visibleRecords.length / CONTRACTOR_RECORDS_PAGE_SIZE));
  const safeRecordPage = Math.min(recordPage, recordPageCount);
  const recordPaginationStart = (safeRecordPage - 1) * CONTRACTOR_RECORDS_PAGE_SIZE;
  const recordPaginationEnd = Math.min(recordPaginationStart + CONTRACTOR_RECORDS_PAGE_SIZE, visibleRecords.length);
  const paginatedVisibleRecords = visibleRecords.slice(recordPaginationStart, recordPaginationEnd);
  const isFormVisible = mode === 'add' || mode === 'view' || mode === 'update';
  const showInlineFormBeforeRecords = canManageContractorPrequalification && isFormVisible;
  const showEmployeeReadOnlyRecord = !canManageContractorPrequalification && mode === 'view' && selectedRecord;
  const isFormReadOnly = mode === 'view' || !canManageContractorPrequalification;
  const isFormEditable = canManageContractorPrequalification && (mode === 'add' || mode === 'update');
  const formPanelTitle = mode === 'add'
    ? 'Add New Pre-Qualified Contractor'
    : mode === 'update'
      ? `Update ${form.companyName || 'Contractor'}`
      : `${form.companyName || 'Contractor'} Record`;

  const expiryAlerts = useMemo(() => records.flatMap((record) => [
    getExpiryAlert(record, 'insuranceExpiry', `${record.companyName} insurance`),
    record.businessLicense === 'Yes' ? getExpiryAlert(record, 'businessLicenseExpiry', `${record.companyName} business license`) : null,
    record.corSecor === 'Yes' ? getExpiryAlert(record, 'corSecorExpiry', `${record.companyName} COR/SECOR`) : null
  ].filter(Boolean)), [records]);

  useEffect(() => {
    if (canManageContractorPrequalification || !selectedRecordId) return;

    const selectedStillVisible = visibleRecords.some((record) => String(record.id || '') === String(selectedRecordId || ''));
    if (!selectedStillVisible) {
      setSelectedRecordId('');
      setForm(getInitialForm());
      setMode('idle');
    }
  }, [canManageContractorPrequalification, selectedRecordId, visibleRecords]);

  useEffect(() => {
    if (recordPage > recordPageCount) {
      setRecordPage(recordPageCount);
    }
  }, [recordPage, recordPageCount]);

  const setField = (field, value) => {
    setNotice('');
    setValidationErrors([]);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateRecordFilter = (value) => {
    setRecordFilter(value);
    setRecordPage(1);
  };

  const resetForm = () => {
    setMode('idle');
    setForm(getInitialForm());
    setSelectedRecordId('');
    setValidationErrors([]);
    setNotice('');
  };

  const startAdd = () => {
    if (!canManageContractorPrequalification) return;
    setMode('add');
    setForm(getInitialForm());
    setSelectedRecordId('');
    setValidationErrors([]);
    setNotice('');
  };

  const loadRecordForView = (recordId) => {
    setSelectedRecordId(recordId);
    const record = records.find((item) => item.id === recordId);
    if (record) {
      setForm({ ...getInitialForm(), ...record });
      setMode('view');
      setValidationErrors([]);
      setNotice('');
    }
  };

  const startUpdate = () => {
    if (!canManageContractorPrequalification) return;
    if (!selectedRecord || selectedRecord.status === 'ARCHIVED') {
      setValidationErrors(['Select an active contractor record to update.']);
      return;
    }
    setForm({ ...getInitialForm(), ...selectedRecord, finalAcknowledgement: false });
    setMode('update');
    setValidationErrors([]);
    setNotice('');
  };

  const addContact = () => {
    setField('additionalContacts', [
      ...form.additionalContacts,
      { name: '', phone: '', email: '' }
    ]);
  };

  const updateContact = (index, field, value) => {
    setField('additionalContacts', form.additionalContacts.map((contact, contactIndex) => (
      contactIndex === index ? { ...contact, [field]: value } : contact
    )));
  };

  const removeContact = (index) => {
    setField('additionalContacts', form.additionalContacts.filter((_, contactIndex) => contactIndex !== index));
  };

  const validateForm = (status) => {
    const errors = [];
    const requireText = (field, label) => {
      if (!String(form[field] || '').trim()) {
        errors.push(`${label} is required.`);
      }
    };

    if (status === 'DRAFT') {
      if (!String(form.companyName || '').trim()) {
        errors.push('Company name is required to save a draft.');
      }
      return errors;
    }

    requireText('dateOfHire', 'Date of hire');
    requireText('companyName', 'Company name');
    requireText('streetAddress', 'Street address');
    requireText('city', 'City');
    requireText('province', 'Province');
    requireText('postalCode', 'Postal code');
    requireText('primaryContactName', 'Primary contact name');
    requireText('primaryContactPhone', 'Primary contact telephone');
    requireText('primaryContactEmail', 'Primary contact email');
    requireText('servicesProvided', 'Describe services provided');
    requireText('insuranceCompany', 'Insurance company');
    requireText('policyNumber', 'Policy number');

    if (form.primaryContactEmail && !isValidEmail(form.primaryContactEmail)) {
      errors.push('Primary contact email must be a valid email address.');
    }

    form.additionalContacts.forEach((contact, index) => {
      const hasAnyValue = contact.name || contact.phone || contact.email;
      if (hasAnyValue && (!contact.name || !contact.phone || !contact.email)) {
        errors.push(`Additional contact ${index + 1} must include name, telephone, and email.`);
      }
      if (contact.email && !isValidEmail(contact.email)) {
        errors.push(`Additional contact ${index + 1} email must be valid.`);
      }
    });

    if (!form.wcbExempt && !String(form.wcbAccountNumber || '').trim()) {
      errors.push('WCB account number is required unless the contractor is exempt.');
    }
    if (form.wcbExempt && !String(form.wcbExemptionExplanation || '').trim()) {
      errors.push('WCB exemption explanation is required.');
    }
    if (form.minimumPolicy === 'No' && !String(form.minimumPolicyExplanation || '').trim()) {
      errors.push('Explain why the insurance policy is below $2,000,000.');
    }
    if (form.addedNamedInsured !== 'Yes') {
      errors.push('Town of Diamond Valley must be listed as an added named insured before approval.');
    }
    if (!datePartsToIso(form.insuranceExpiry)) {
      errors.push('Insurance expiry date is required.');
    }
    if (form.businessLicense === 'Yes') {
      requireText('businessLicenseNumber', 'Business license number');
      if (!datePartsToIso(form.businessLicenseExpiry)) {
        errors.push('Business license expiry date is required.');
      }
    } else if (!String(form.businessLicenseExemption || '').trim()) {
      errors.push('Business license exemption explanation is required when the contractor has no Town business license.');
    }
    if (form.corSecor === 'Yes') {
      requireText('corSecorNumber', 'COR/SECOR number');
      if (!datePartsToIso(form.corSecorExpiry)) {
        errors.push('COR/SECOR expiry date is required.');
      }
      if (form.programElementsEvaluation === 'No' && !String(form.programElementsEvaluationNote || '').trim()) {
        errors.push('Program elements evaluation note is required when evaluation is No.');
      }
    }
    if (form.primeContractor === 'Yes' && form.primeContractorAttachments.length === 0) {
      errors.push('Prime contractor supporting attachment is required when Prime Contractor is Yes.');
    }
    if (form.primeContractor === 'No' && !String(form.primeContractorComments || '').trim()) {
      errors.push('Prime contractor comment is required when Prime Contractor is No.');
    }
    if (form.trainingCertificatesReviewed === 'Yes' && form.trainingCertificateAttachments.length === 0) {
      errors.push('Safety training certificate attachments are required when certificates were reviewed.');
    }
    if (form.trainingCertificatesReviewed === 'No' && !String(form.trainingVerificationExplanation || '').trim()) {
      errors.push('Explain why specific safety training verification is not required.');
    }
    if (form.extraordinaryHazards === 'Yes') {
      requireText('preExistingHazards', 'Pre-existing hazards');
      requireText('hazardControls', 'Hazard controls');
    }
    if (form.generalAttachments.length === 0) {
      errors.push('At least one mandatory supporting document must be attached before approval.');
    }
    if (!form.finalAcknowledgement) {
      errors.push('Final acknowledgement and sign-off is required before approval.');
    }

    return errors;
  };

  const buildRecord = (status) => {
    const timestamp = new Date().toISOString();
    const existingRecord = form.id ? records.find((record) => record.id === form.id) : null;
    return {
      ...form,
      id: form.id || makeRecordId(),
      status,
      createdAt: existingRecord?.createdAt || timestamp,
      updatedAt: timestamp,
      updatedBy: userName,
      updatedByEmail: userEmail,
      approvedAt: status === 'APPROVED' ? timestamp : existingRecord?.approvedAt || '',
      approvedBy: status === 'APPROVED' ? userName : existingRecord?.approvedBy || '',
      actionType: mode
    };
  };

  const saveRecord = async (status) => {
    if (!isFormEditable) return;

    const errors = validateForm(status);
    if (errors.length) {
      setValidationErrors(errors);
      setNotice('');
      return;
    }

    const isExistingRecord = !!form.id;
    const record = buildRecord(status);
    setIsSaving(true);
    try {
      const response = isExistingRecord
        ? await API.put(`/api/contractorPrequalification/records/${encodeURIComponent(record.id)}`, { record, status })
        : await API.post('/api/contractorPrequalification/records', { record, status });

      if (!response.data?.success || !response.data?.record) {
        throw new Error(response.data?.error || 'Unable to save contractor record.');
      }

      const savedRecord = response.data.record;
      setRecords((currentRecords) => {
        const exists = currentRecords.some((item) => item.id === savedRecord.id);
        if (exists) {
          return currentRecords.map((item) => item.id === savedRecord.id ? savedRecord : item);
        }
        return [savedRecord, ...currentRecords];
      });
      setForm({ ...getInitialForm(), ...savedRecord });
      setSelectedRecordId(savedRecord.id);
      setRecordsTab('active');
      setMode('view');
      setValidationErrors([]);
      setNotice(status === 'DRAFT' ? 'Draft saved. It will not appear on contractor drop-down lists until approved.' : 'Contractor approved and ready for Daily Pre-job, Formal Hazard Assessment, and Toolbox Meeting contractor lists.');
    } catch (error) {
      setValidationErrors([error?.response?.data?.error || error.message || 'Unable to save contractor record.']);
      setNotice('');
    } finally {
      setIsSaving(false);
    }
  };

  const archiveRecord = async () => {
    if (!canManageContractorPrequalification) return;
    if (!selectedRecord || selectedRecord.status === 'ARCHIVED') {
      setValidationErrors(['Select an active contractor record to archive.']);
      return;
    }

    const confirmed = window.confirm(`Archive ${selectedRecord.companyName || 'this contractor'}? This will move the record to the Archived tab.`);
    if (!confirmed) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await API.post(`/api/contractorPrequalification/records/${encodeURIComponent(selectedRecord.id)}/archive`);
      if (!response.data?.success || !response.data?.record) {
        throw new Error(response.data?.error || 'Unable to archive contractor record.');
      }

      const archivedRecord = response.data.record;
      setRecords((currentRecords) => currentRecords.map((record) => record.id === selectedRecord.id ? archivedRecord : record));
      setForm({ ...getInitialForm(), ...archivedRecord });
      setSelectedRecordId(archivedRecord.id);
      setRecordsTab('archived');
      setMode('view');
      setValidationErrors([]);
      setNotice('Contractor archived. The record remains retained for audit and investigation purposes.');
    } catch (error) {
      setValidationErrors([error?.response?.data?.error || error.message || 'Unable to archive contractor record.']);
      setNotice('');
    } finally {
      setIsSaving(false);
    }
  };

  const changeRecordsTab = (nextTab) => {
    if (!canManageContractorPrequalification) return;
    setRecordsTab(nextTab);
    setRecordPage(1);
    setMode('idle');
    setForm(getInitialForm());
    setSelectedRecordId('');
    setValidationErrors([]);
    setNotice('');
  };

  const recordStatusTone = (status) => {
    if (status === 'APPROVED') return 'teal';
    if (status === 'ARCHIVED') return 'neutral';
    return 'warning';
  };

  return (
    <main style={page}>
      <div style={shell}>
        <section style={{ ...card, borderTop: `4px solid ${TEAL}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0, flex: '1 1 520px' }}>
              <div style={{ fontSize: '12px', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                Health and Safety Workflow
              </div>
              <h1 style={{ fontSize: '28px', lineHeight: 1.2, margin: '0 0 8px', color: TEXT }}>
                Pre-qualification of Contractors
              </h1>
              <p style={{ ...paragraph, marginBottom: 0 }}>
                {canManageContractorPrequalification
                  ? 'Add, update, approve, or archive contractors, service providers, and prime contractors used by the Town of Diamond Valley.'
                  : 'Search approved contractor records and open a read-only record for review.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {canManageContractorPrequalification ? <Chip tone="warning">Draft allowed</Chip> : <Chip tone="neutral">Employee View</Chip>}
              <Chip tone="teal">{canManageContractorPrequalification ? 'Approval requires attachments' : 'Read only'}</Chip>
              <Chip tone="blue">Expiry tracking</Chip>
            </div>
          </div>
        </section>

        <section style={grid}>
          <div style={card}>
            <h2 style={sectionTitle}>Workflow User</h2>
            <div style={compactGrid}>
              <div>
                <span style={labelStyle}>Updated by</span>
                <div style={{ fontWeight: 700 }}>{userName}</div>
                {userEmail && <div style={{ fontSize: '12px', color: MUTED }}>{userEmail}</div>}
              </div>
              <div>
                <span style={labelStyle}>Current date/time</span>
                <div style={{ fontWeight: 700 }}>{new Date().toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div style={card}>
            <h2 style={sectionTitle}>Contractor List Rules</h2>
            <p style={paragraph}>Approved records can be referenced by Daily Pre-job Hazard Assessment, Formal Hazard Assessment, and Toolbox Meeting contractor lists.</p>
            <p style={{ ...paragraph, marginBottom: 0 }}>Draft records stay out of those lists until mandatory requirements and final sign-off are complete.</p>
          </div>
        </section>

        {canManageContractorPrequalification && (
          <section style={{ ...card, display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ ...sectionTitle, marginBottom: '4px' }}>Contractor Intake</h2>
              <p style={{ ...paragraph, marginBottom: 0 }}>Create a new pre-qualified contractor record for the approved contractor lists.</p>
            </div>
            <button
              type="button"
              disabled={isSaving}
              onClick={startAdd}
              style={{ ...buttonBase, background: mode === 'add' ? TEAL_DARK : TEAL, color: '#fff', borderColor: mode === 'add' ? TEAL_DARK : TEAL, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
            >
              Add New Pre-Qualified Contractor
            </button>
          </section>
        )}

        {expiryAlerts.length > 0 && (
          <section style={{ ...card, borderColor: '#fde68a', background: WARNING_BG }}>
            <h2 style={{ ...sectionTitle, color: WARNING }}>Expiry Notifications</h2>
            <ul style={{ margin: 0, paddingLeft: '18px', color: WARNING, fontSize: '14px' }}>
              {expiryAlerts.map((alert) => <li key={alert}>{alert}</li>)}
            </ul>
          </section>
        )}

        {showInlineFormBeforeRecords && (
          <>
            <section style={{ ...card, borderLeft: `4px solid ${isFormReadOnly ? BORDER : TEAL}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <h2 style={{ ...sectionTitle, marginBottom: '4px' }}>{formPanelTitle}</h2>
                  <p style={{ ...paragraph, marginBottom: 0 }}>{isFormReadOnly ? 'Read-only contractor record.' : 'Editable contractor pre-qualification form.'}</p>
                </div>
                <Chip tone={isFormReadOnly ? 'neutral' : 'teal'}>{isFormReadOnly ? 'Read Only' : 'Editable'}</Chip>
              </div>
            </section>
            <FormSection title="Company Information" note="Company details and services">
              <div style={grid}>
                <TextInput label="Date of hire" type="date" value={form.dateOfHire} onChange={(value) => setField('dateOfHire', value)} required disabled={isFormReadOnly} />
                <TextInput label="Company name" value={form.companyName} onChange={(value) => setField('companyName', value)} required disabled={isFormReadOnly} />
                <TextInput label="Street address" value={form.streetAddress} onChange={(value) => setField('streetAddress', value)} required disabled={isFormReadOnly} />
                <TextInput label="City" value={form.city} onChange={(value) => setField('city', value)} required disabled={isFormReadOnly} />
                <SelectInput label="Province" value={form.province} onChange={(value) => setField('province', value)} options={provinces} required disabled={isFormReadOnly} />
                <TextInput label="Postal code" value={form.postalCode} onChange={(value) => setField('postalCode', value)} required disabled={isFormReadOnly} />
              </div>
              <div style={{ marginTop: '14px' }}>
                <TextArea label="Describe services provided" value={form.servicesProvided} onChange={(value) => setField('servicesProvided', value)} required disabled={isFormReadOnly} />
              </div>
            </FormSection>

            <FormSection title="Contacts" note="Primary contact is mandatory; additional contacts are optional">
              <div style={grid}>
                <TextInput label="Primary contact name" value={form.primaryContactName} onChange={(value) => setField('primaryContactName', value)} required disabled={isFormReadOnly} />
                <TextInput label="Primary contact telephone" value={form.primaryContactPhone} onChange={(value) => setField('primaryContactPhone', value)} required placeholder="(###) ###-####" disabled={isFormReadOnly} />
                <TextInput label="Primary contact email" type="email" value={form.primaryContactEmail} onChange={(value) => setField('primaryContactEmail', value)} required disabled={isFormReadOnly} />
              </div>
              {form.additionalContacts.length > 0 && (
                <div style={{ marginTop: '14px', display: 'grid', gap: '12px' }}>
                  {form.additionalContacts.map((contact, index) => (
                    <div key={index} style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                        <strong style={{ fontSize: '14px' }}>Additional contact {index + 1}</strong>
                        {!isFormReadOnly && <button type="button" onClick={() => removeContact(index)} style={{ ...buttonBase, padding: '5px 9px', color: DANGER, background: SURFACE }}>Remove</button>}
                      </div>
                      <div style={grid}>
                        <TextInput label="Name" value={contact.name} onChange={(value) => updateContact(index, 'name', value)} disabled={isFormReadOnly} />
                        <TextInput label="Telephone" value={contact.phone} onChange={(value) => updateContact(index, 'phone', value)} disabled={isFormReadOnly} />
                        <TextInput label="Email" type="email" value={contact.email} onChange={(value) => updateContact(index, 'email', value)} disabled={isFormReadOnly} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!isFormReadOnly && (
                <button type="button" onClick={addContact} style={{ ...buttonBase, marginTop: '12px', background: '#eff6ff', color: BLUE, borderColor: '#bfdbfe' }}>
                  Add Contact
                </button>
              )}
            </FormSection>

            <FormSection title="WCB and Insurance" note="Insurance expiry drives notification reminders">
              <div style={grid}>
                <TextInput label="WCB account number" value={form.wcbAccountNumber} onChange={(value) => setField('wcbAccountNumber', value)} required={!form.wcbExempt} disabled={isFormReadOnly} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '22px', fontSize: '14px', color: TEXT }}>
                  <input type="checkbox" checked={form.wcbExempt} disabled={isFormReadOnly} onChange={(event) => setField('wcbExempt', event.target.checked)} style={{ accentColor: TEAL }} />
                  WCB exempt
                </label>
                {form.wcbExempt && <TextInput label="WCB exemption explanation" value={form.wcbExemptionExplanation} onChange={(value) => setField('wcbExemptionExplanation', value)} required disabled={isFormReadOnly} />}
                <TextInput label="Insurance company" value={form.insuranceCompany} onChange={(value) => setField('insuranceCompany', value)} required disabled={isFormReadOnly} />
                <TextInput label="Policy number" value={form.policyNumber} onChange={(value) => setField('policyNumber', value)} required disabled={isFormReadOnly} />
                <YesNoField label="Minimum $2,000,000 policy" value={form.minimumPolicy} onChange={(value) => setField('minimumPolicy', value)} disabled={isFormReadOnly} />
                {form.minimumPolicy === 'No' && <TextInput label="Explanation for policy below minimum" value={form.minimumPolicyExplanation} onChange={(value) => setField('minimumPolicyExplanation', value)} required disabled={isFormReadOnly} />}
                <YesNoField label="Town of Diamond Valley listed as added named insured" value={form.addedNamedInsured} onChange={(value) => setField('addedNamedInsured', value)} disabled={isFormReadOnly} />
                {form.addedNamedInsured === 'No' && <TextInput label="Certificate follow-up note" value={form.addedNamedInsuredNote} onChange={(value) => setField('addedNamedInsuredNote', value)} placeholder="Record remains Draft until certificate is received." disabled={isFormReadOnly} />}
                <DatePartsInput label="Insurance expiry date" value={form.insuranceExpiry} onChange={(value) => setField('insuranceExpiry', value)} required disabled={isFormReadOnly} />
              </div>
            </FormSection>

            <FormSection title="Town Business License and COR/SECOR" note="Expiry fields trigger 30-day Safety reminders">
              <div style={grid}>
                <YesNoField label="Town business license" value={form.businessLicense} onChange={(value) => setField('businessLicense', value)} disabled={isFormReadOnly} />
                {form.businessLicense === 'Yes' ? (
                  <>
                    <TextInput label="Business license number" value={form.businessLicenseNumber} onChange={(value) => setField('businessLicenseNumber', value)} required disabled={isFormReadOnly} />
                    <DatePartsInput label="Business license expiry date" value={form.businessLicenseExpiry} onChange={(value) => setField('businessLicenseExpiry', value)} required disabled={isFormReadOnly} />
                  </>
                ) : (
                  <TextArea label="Business license exemption explanation" value={form.businessLicenseExemption} onChange={(value) => setField('businessLicenseExemption', value)} required placeholder="Example: not required due to contract or qualified professional association membership." disabled={isFormReadOnly} />
                )}
                <YesNoField label="COR/SECOR certificate" value={form.corSecor} onChange={(value) => setField('corSecor', value)} disabled={isFormReadOnly} />
                {form.corSecor === 'Yes' && (
                  <>
                    <TextInput label="COR/SECOR number" value={form.corSecorNumber} onChange={(value) => setField('corSecorNumber', value)} required disabled={isFormReadOnly} />
                    <DatePartsInput label="COR/SECOR expiry date" value={form.corSecorExpiry} onChange={(value) => setField('corSecorExpiry', value)} required disabled={isFormReadOnly} />
                    <YesNoField label="Program elements evaluation needed" value={form.programElementsEvaluation} onChange={(value) => setField('programElementsEvaluation', value)} disabled={isFormReadOnly} />
                    {form.programElementsEvaluation === 'No' && <TextArea label="Program elements evaluation note" value={form.programElementsEvaluationNote} onChange={(value) => setField('programElementsEvaluationNote', value)} required disabled={isFormReadOnly} />}
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title="Prime Contractor, Training, and Hazards" note="Conditional attachments and explanations are enforced before approval">
              <div style={grid}>
                <YesNoField label="Prime contractor" value={form.primeContractor} onChange={(value) => setField('primeContractor', value)} disabled={isFormReadOnly} />
                {form.primeContractor === 'Yes' ? (
                  <FileInput label="Prime contractor attachment" files={form.primeContractorAttachments} onChange={(value) => setField('primeContractorAttachments', value)} required disabled={isFormReadOnly} />
                ) : (
                  <TextArea label="Prime contractor comments" value={form.primeContractorComments} onChange={(value) => setField('primeContractorComments', value)} required placeholder="Contractor will not be assigned the Prime Contractor role." disabled={isFormReadOnly} />
                )}
                <YesNoField label="Workers' safety training certificates reviewed" value={form.trainingCertificatesReviewed} onChange={(value) => setField('trainingCertificatesReviewed', value)} disabled={isFormReadOnly} />
                {form.trainingCertificatesReviewed === 'Yes' ? (
                  <FileInput label="Safety training certificates" files={form.trainingCertificateAttachments} onChange={(value) => setField('trainingCertificateAttachments', value)} required disabled={isFormReadOnly} />
                ) : (
                  <TextArea label="Training verification explanation" value={form.trainingVerificationExplanation} onChange={(value) => setField('trainingVerificationExplanation', value)} required placeholder="Explain job details and why specific safety training verification is not required." disabled={isFormReadOnly} />
                )}
                <YesNoField label="Extraordinary pre-existing hazards and controls" value={form.extraordinaryHazards} onChange={(value) => setField('extraordinaryHazards', value)} disabled={isFormReadOnly} />
                {form.extraordinaryHazards === 'Yes' && (
                  <>
                    <FileInput label="Contractor Formal Hazard Assessment report" files={form.formalHazardAssessmentAttachments} onChange={(value) => setField('formalHazardAssessmentAttachments', value)} disabled={isFormReadOnly} />
                    <TextArea label="Pre-existing hazards" value={form.preExistingHazards} onChange={(value) => setField('preExistingHazards', value)} required disabled={isFormReadOnly} />
                    <TextArea label="Hazard controls" value={form.hazardControls} onChange={(value) => setField('hazardControls', value)} required disabled={isFormReadOnly} />
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title="Attachments and Final Sign-off" note="Approval requires mandatory attachments and acknowledgement">
              <div style={grid}>
                <FileInput label="Mandatory supporting documents" files={form.generalAttachments} onChange={(value) => setField('generalAttachments', value)} required disabled={isFormReadOnly} />
                <div>
                  <span style={labelStyle}>Approved contractor lists</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {destinationLists.map((destination) => <Chip key={destination} tone="teal">{destination}</Chip>)}
                  </div>
                </div>
              </div>
              <label style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', gap: '8px', alignItems: 'flex-start', marginTop: '14px', fontSize: '14px', lineHeight: 1.5 }}>
                <input type="checkbox" checked={form.finalAcknowledgement} disabled={isFormReadOnly} onChange={(event) => setField('finalAcknowledgement', event.target.checked)} style={{ accentColor: TEAL, marginTop: '3px' }} />
                <span>The Health and Safety Advisor, or approved contractor-management role, acknowledges that the information is correct and complete.</span>
              </label>
            </FormSection>

            {isFormEditable && (
              <section style={card}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button type="button" disabled={isSaving} onClick={() => saveRecord('DRAFT')} style={{ ...buttonBase, background: WARNING_BG, color: WARNING, borderColor: '#fde68a', opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button type="button" disabled={isSaving} onClick={() => saveRecord('APPROVED')} style={{ ...buttonBase, background: TEAL, color: '#fff', borderColor: TEAL, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
                    {isSaving ? 'Saving...' : 'Submit Approved Contractor'}
                  </button>
                  <button type="button" disabled={isSaving} onClick={resetForm} style={{ ...buttonBase, background: SURFACE, color: TEXT, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
                    Clear Form
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {validationErrors.length > 0 && (
          <section style={{ ...card, borderColor: '#fecaca', background: DANGER_BG }}>
            <h2 style={{ ...sectionTitle, color: DANGER }}>Required Before Approval</h2>
            <ul style={{ margin: 0, paddingLeft: '18px', color: DANGER, fontSize: '14px' }}>
              {validationErrors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          </section>
        )}

        {notice && (
          <section style={{ ...card, borderColor: TEAL, background: TEAL_BG, color: TEAL_DARK }}>
            <strong>{notice}</strong>
          </section>
        )}

        <section style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <h2 style={{ ...sectionTitle, marginBottom: 0 }}>Contractor Records</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {canManageContractorPrequalification ? (
                <>
                  <Chip tone="teal">{activeRecords.filter((record) => record.status === 'APPROVED').length} approved</Chip>
                  <Chip tone="warning">{activeRecords.filter((record) => record.status === 'DRAFT').length} draft</Chip>
                  <Chip>{archivedRecords.length} archived</Chip>
                </>
              ) : (
                <Chip tone="teal">{records.length} approved contractors</Chip>
              )}
            </div>
          </div>

          {canManageContractorPrequalification ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {[
                { id: 'active', label: `Active Contractors (${activeRecords.length})` },
                { id: 'archived', label: `Archived Contractors (${archivedRecords.length})` }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeRecordsTab(tab.id)}
                  style={{
                    ...buttonBase,
                    background: recordsTab === tab.id ? TEAL : SURFACE,
                    color: recordsTab === tab.id ? '#fff' : TEXT,
                    borderColor: recordsTab === tab.id ? TEAL : BORDER
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}

          <div style={{ marginBottom: '12px' }}>
            <TextInput
              label="Search contractor records"
              value={recordFilter}
              onChange={updateRecordFilter}
              placeholder="Search company, contact, service, city, license, or COR/SECOR"
            />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', color: MUTED }}>
                  <th style={{ textAlign: 'left', padding: '9px', borderBottom: `1px solid ${BORDER}` }}>Company</th>
                  <th style={{ textAlign: 'left', padding: '9px', borderBottom: `1px solid ${BORDER}` }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '9px', borderBottom: `1px solid ${BORDER}` }}>Primary Contact</th>
                  <th style={{ textAlign: 'left', padding: '9px', borderBottom: `1px solid ${BORDER}` }}>Insurance Expiry</th>
                  <th style={{ textAlign: 'left', padding: '9px', borderBottom: `1px solid ${BORDER}` }}>Updated By</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingRecords ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '14px 9px', color: MUTED }}>Loading contractor records...</td>
                  </tr>
                ) : visibleRecords.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '14px 9px', color: MUTED }}>
                      {recordFilter.trim()
                        ? 'No contractor records match the current search.'
                        : canManageContractorPrequalification
                          ? (recordsTab === 'archived' ? 'No archived contractor records.' : 'No active contractor records have been saved yet.')
                          : 'No approved contractor records are available.'}
                    </td>
                  </tr>
                ) : paginatedVisibleRecords.map((record) => {
                  const isSelected = String(selectedRecordId || '') === String(record.id || '');
                  return (
                  <tr
                    key={record.id}
                    onClick={() => loadRecordForView(record.id)}
                    style={{ cursor: 'pointer', background: isSelected ? TEAL_BG : 'transparent' }}
                  >
                    <td style={{ padding: '9px', borderBottom: `1px solid ${BORDER}`, fontWeight: 700 }}>{record.companyName}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${BORDER}` }}><Chip tone={recordStatusTone(record.status)}>{record.status}</Chip></td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${BORDER}` }}>{record.primaryContactName || 'Not set'}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${BORDER}` }}>{formatDateParts(record.insuranceExpiry)}</td>
                    <td style={{ padding: '9px', borderBottom: `1px solid ${BORDER}` }}>{record.updatedBy || 'Not set'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {visibleRecords.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: MUTED }}>
                Showing {recordPaginationStart + 1}-{recordPaginationEnd} of {visibleRecords.length} contractor record{visibleRecords.length === 1 ? '' : 's'}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={safeRecordPage <= 1}
                  onClick={() => setRecordPage((currentPage) => Math.max(1, currentPage - 1))}
                  style={{ ...buttonBase, background: SURFACE, color: TEXT, opacity: safeRecordPage <= 1 ? 0.55 : 1, cursor: safeRecordPage <= 1 ? 'not-allowed' : 'pointer' }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '12px', color: MUTED }}>
                  Page {safeRecordPage} of {recordPageCount}
                </span>
                <button
                  type="button"
                  disabled={safeRecordPage >= recordPageCount}
                  onClick={() => setRecordPage((currentPage) => Math.min(recordPageCount, currentPage + 1))}
                  style={{ ...buttonBase, background: SURFACE, color: TEXT, opacity: safeRecordPage >= recordPageCount ? 0.55 : 1, cursor: safeRecordPage >= recordPageCount ? 'not-allowed' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {canManageContractorPrequalification ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: '12px', color: MUTED }}>
              {selectedRecord ? `Selected: ${selectedRecord.companyName || 'Contractor'} (${selectedRecord.status})` : 'Select an active contractor record to update or archive.'}
            </span>
            {selectedRecord && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={isSaving || selectedRecord.status === 'ARCHIVED'}
                  onClick={startUpdate}
                  style={{ ...buttonBase, background: SURFACE, color: TEXT, opacity: isSaving || selectedRecord.status === 'ARCHIVED' ? 0.55 : 1, cursor: isSaving || selectedRecord.status === 'ARCHIVED' ? 'not-allowed' : 'pointer' }}
                >
                  Update
                </button>
                <button
                  type="button"
                  disabled={isSaving || selectedRecord.status === 'ARCHIVED'}
                  onClick={archiveRecord}
                  style={{ ...buttonBase, background: DANGER, color: '#fff', borderColor: DANGER, opacity: isSaving || selectedRecord.status === 'ARCHIVED' ? 0.55 : 1, cursor: isSaving || selectedRecord.status === 'ARCHIVED' ? 'not-allowed' : 'pointer' }}
                >
                  {isSaving ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            )}
          </div>
          ) : (
            <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${BORDER}`, fontSize: '12px', color: MUTED }}>
              {selectedRecord ? `Selected for read-only review: ${selectedRecord.companyName || 'Contractor'}` : 'Select a contractor record to view read-only details underneath this list.'}
            </div>
          )}
        </section>

        {showEmployeeReadOnlyRecord && (
          <section style={{ ...card, borderLeft: `4px solid ${BORDER}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <h2 style={{ ...sectionTitle, marginBottom: '4px' }}>{form.companyName || 'Contractor'} Record</h2>
                <p style={{ ...paragraph, marginBottom: 0 }}>Read-only contractor record.</p>
              </div>
              <Chip tone="neutral">Read Only</Chip>
            </div>
          </section>
        )}

        {showEmployeeReadOnlyRecord && (
          <>
            <FormSection title="Company Information" note="Company details and services">
              <div style={grid}>
                <TextInput label="Date of hire" type="date" value={form.dateOfHire} onChange={(value) => setField('dateOfHire', value)} required disabled={isFormReadOnly} />
                <TextInput label="Company name" value={form.companyName} onChange={(value) => setField('companyName', value)} required disabled={isFormReadOnly} />
                <TextInput label="Street address" value={form.streetAddress} onChange={(value) => setField('streetAddress', value)} required disabled={isFormReadOnly} />
                <TextInput label="City" value={form.city} onChange={(value) => setField('city', value)} required disabled={isFormReadOnly} />
                <SelectInput label="Province" value={form.province} onChange={(value) => setField('province', value)} options={provinces} required disabled={isFormReadOnly} />
                <TextInput label="Postal code" value={form.postalCode} onChange={(value) => setField('postalCode', value)} required disabled={isFormReadOnly} />
              </div>
              <div style={{ marginTop: '14px' }}>
                <TextArea label="Describe services provided" value={form.servicesProvided} onChange={(value) => setField('servicesProvided', value)} required disabled={isFormReadOnly} />
              </div>
            </FormSection>

            <FormSection title="Contacts" note="Primary contact is mandatory; additional contacts are optional">
              <div style={grid}>
                <TextInput label="Primary contact name" value={form.primaryContactName} onChange={(value) => setField('primaryContactName', value)} required disabled={isFormReadOnly} />
                <TextInput label="Primary contact telephone" value={form.primaryContactPhone} onChange={(value) => setField('primaryContactPhone', value)} required placeholder="(###) ###-####" disabled={isFormReadOnly} />
                <TextInput label="Primary contact email" type="email" value={form.primaryContactEmail} onChange={(value) => setField('primaryContactEmail', value)} required disabled={isFormReadOnly} />
              </div>
              {form.additionalContacts.length > 0 && (
                <div style={{ marginTop: '14px', display: 'grid', gap: '12px' }}>
                  {form.additionalContacts.map((contact, index) => (
                    <div key={index} style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                        <strong style={{ fontSize: '14px' }}>Additional contact {index + 1}</strong>
                      </div>
                      <div style={grid}>
                        <TextInput label="Name" value={contact.name} onChange={(value) => updateContact(index, 'name', value)} disabled={isFormReadOnly} />
                        <TextInput label="Telephone" value={contact.phone} onChange={(value) => updateContact(index, 'phone', value)} disabled={isFormReadOnly} />
                        <TextInput label="Email" type="email" value={contact.email} onChange={(value) => updateContact(index, 'email', value)} disabled={isFormReadOnly} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FormSection>

            <FormSection title="WCB and Insurance" note="Insurance expiry drives notification reminders">
              <div style={grid}>
                <TextInput label="WCB account number" value={form.wcbAccountNumber} onChange={(value) => setField('wcbAccountNumber', value)} required={!form.wcbExempt} disabled={isFormReadOnly} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '22px', fontSize: '14px', color: TEXT }}>
                  <input type="checkbox" checked={form.wcbExempt} disabled={isFormReadOnly} onChange={(event) => setField('wcbExempt', event.target.checked)} style={{ accentColor: TEAL }} />
                  WCB exempt
                </label>
                {form.wcbExempt && <TextInput label="WCB exemption explanation" value={form.wcbExemptionExplanation} onChange={(value) => setField('wcbExemptionExplanation', value)} required disabled={isFormReadOnly} />}
                <TextInput label="Insurance company" value={form.insuranceCompany} onChange={(value) => setField('insuranceCompany', value)} required disabled={isFormReadOnly} />
                <TextInput label="Policy number" value={form.policyNumber} onChange={(value) => setField('policyNumber', value)} required disabled={isFormReadOnly} />
                <YesNoField label="Minimum $2,000,000 policy" value={form.minimumPolicy} onChange={(value) => setField('minimumPolicy', value)} disabled={isFormReadOnly} />
                {form.minimumPolicy === 'No' && <TextInput label="Explanation for policy below minimum" value={form.minimumPolicyExplanation} onChange={(value) => setField('minimumPolicyExplanation', value)} required disabled={isFormReadOnly} />}
                <YesNoField label="Town of Diamond Valley listed as added named insured" value={form.addedNamedInsured} onChange={(value) => setField('addedNamedInsured', value)} disabled={isFormReadOnly} />
                {form.addedNamedInsured === 'No' && <TextInput label="Certificate follow-up note" value={form.addedNamedInsuredNote} onChange={(value) => setField('addedNamedInsuredNote', value)} placeholder="Record remains Draft until certificate is received." disabled={isFormReadOnly} />}
                <DatePartsInput label="Insurance expiry date" value={form.insuranceExpiry} onChange={(value) => setField('insuranceExpiry', value)} required disabled={isFormReadOnly} />
              </div>
            </FormSection>

            <FormSection title="Town Business License and COR/SECOR" note="Expiry fields trigger 30-day Safety reminders">
              <div style={grid}>
                <YesNoField label="Town business license" value={form.businessLicense} onChange={(value) => setField('businessLicense', value)} disabled={isFormReadOnly} />
                {form.businessLicense === 'Yes' ? (
                  <>
                    <TextInput label="Business license number" value={form.businessLicenseNumber} onChange={(value) => setField('businessLicenseNumber', value)} required disabled={isFormReadOnly} />
                    <DatePartsInput label="Business license expiry date" value={form.businessLicenseExpiry} onChange={(value) => setField('businessLicenseExpiry', value)} required disabled={isFormReadOnly} />
                  </>
                ) : (
                  <TextArea label="Business license exemption explanation" value={form.businessLicenseExemption} onChange={(value) => setField('businessLicenseExemption', value)} required placeholder="Example: not required due to contract or qualified professional association membership." disabled={isFormReadOnly} />
                )}
                <YesNoField label="COR/SECOR certificate" value={form.corSecor} onChange={(value) => setField('corSecor', value)} disabled={isFormReadOnly} />
                {form.corSecor === 'Yes' && (
                  <>
                    <TextInput label="COR/SECOR number" value={form.corSecorNumber} onChange={(value) => setField('corSecorNumber', value)} required disabled={isFormReadOnly} />
                    <DatePartsInput label="COR/SECOR expiry date" value={form.corSecorExpiry} onChange={(value) => setField('corSecorExpiry', value)} required disabled={isFormReadOnly} />
                    <YesNoField label="Program elements evaluation needed" value={form.programElementsEvaluation} onChange={(value) => setField('programElementsEvaluation', value)} disabled={isFormReadOnly} />
                    {form.programElementsEvaluation === 'No' && <TextArea label="Program elements evaluation note" value={form.programElementsEvaluationNote} onChange={(value) => setField('programElementsEvaluationNote', value)} required disabled={isFormReadOnly} />}
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title="Prime Contractor, Training, and Hazards" note="Conditional attachments and explanations are enforced before approval">
              <div style={grid}>
                <YesNoField label="Prime contractor" value={form.primeContractor} onChange={(value) => setField('primeContractor', value)} disabled={isFormReadOnly} />
                {form.primeContractor === 'Yes' ? (
                  <FileInput label="Prime contractor attachment" files={form.primeContractorAttachments} onChange={(value) => setField('primeContractorAttachments', value)} required disabled={isFormReadOnly} />
                ) : (
                  <TextArea label="Prime contractor comments" value={form.primeContractorComments} onChange={(value) => setField('primeContractorComments', value)} required placeholder="Contractor will not be assigned the Prime Contractor role." disabled={isFormReadOnly} />
                )}
                <YesNoField label="Workers' safety training certificates reviewed" value={form.trainingCertificatesReviewed} onChange={(value) => setField('trainingCertificatesReviewed', value)} disabled={isFormReadOnly} />
                {form.trainingCertificatesReviewed === 'Yes' ? (
                  <FileInput label="Safety training certificates" files={form.trainingCertificateAttachments} onChange={(value) => setField('trainingCertificateAttachments', value)} required disabled={isFormReadOnly} />
                ) : (
                  <TextArea label="Training verification explanation" value={form.trainingVerificationExplanation} onChange={(value) => setField('trainingVerificationExplanation', value)} required placeholder="Explain job details and why specific safety training verification is not required." disabled={isFormReadOnly} />
                )}
                <YesNoField label="Extraordinary pre-existing hazards and controls" value={form.extraordinaryHazards} onChange={(value) => setField('extraordinaryHazards', value)} disabled={isFormReadOnly} />
                {form.extraordinaryHazards === 'Yes' && (
                  <>
                    <FileInput label="Contractor Formal Hazard Assessment report" files={form.formalHazardAssessmentAttachments} onChange={(value) => setField('formalHazardAssessmentAttachments', value)} disabled={isFormReadOnly} />
                    <TextArea label="Pre-existing hazards" value={form.preExistingHazards} onChange={(value) => setField('preExistingHazards', value)} required disabled={isFormReadOnly} />
                    <TextArea label="Hazard controls" value={form.hazardControls} onChange={(value) => setField('hazardControls', value)} required disabled={isFormReadOnly} />
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title="Attachments and Final Sign-off" note="Approval requires mandatory attachments and acknowledgement">
              <div style={grid}>
                <FileInput label="Mandatory supporting documents" files={form.generalAttachments} onChange={(value) => setField('generalAttachments', value)} required disabled={isFormReadOnly} />
                <div>
                  <span style={labelStyle}>Approved contractor lists</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {destinationLists.map((destination) => <Chip key={destination} tone="teal">{destination}</Chip>)}
                  </div>
                </div>
              </div>
              <label style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', gap: '8px', alignItems: 'flex-start', marginTop: '14px', fontSize: '14px', lineHeight: 1.5 }}>
                <input type="checkbox" checked={form.finalAcknowledgement} disabled={isFormReadOnly} onChange={(event) => setField('finalAcknowledgement', event.target.checked)} style={{ accentColor: TEAL, marginTop: '3px' }} />
                <span>The Health and Safety Advisor, or approved contractor-management role, acknowledges that the information is correct and complete.</span>
              </label>
            </FormSection>
          </>
        )}
      </div>
    </main>
  );
}